import { useTranslation } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { RulebookModerationFix } from "@next-platform/packages-ts-types";
import React, { memo, useMemo } from "react";

type RulebookListCardModerationRowProps = {
  moderationFix: RulebookModerationFix;
  "data-testid"?: string;
};

const RulebookListCardModerationRow = ({
  moderationFix,
  "data-testid": testId = "RulebookListCardModerationRow",
}: RulebookListCardModerationRowProps) => {
  const { t: translate } = useTranslation();
  const reasons = useMemo(() => moderationFix.reasons.join(", "), [moderationFix.reasons]);
  const messageValues = useMemo(() => ({ reasons: reasons || moderationFix.note || "" }), [reasons, moderationFix.note]);
  const message =
    reasons || moderationFix.note
      ? translate("rulebook:card.changesRequested", "Changes requested: {{reasons}}", messageValues)
      : translate("rulebook:card.changesRequestedGeneric", "Changes requested");
  const flaggedTextFallback = moderationFix.num_flagged_texts === 1 ? "{{count}} flagged text" : "{{count}} flagged texts";
  const flaggedTextLabel = useMemo(
    () =>
      moderationFix.num_flagged_texts > 0
        ? translate("rulebook:card.flaggedTexts", flaggedTextFallback, { count: moderationFix.num_flagged_texts })
        : "",
    [moderationFix.num_flagged_texts, translate, flaggedTextFallback],
  );
  const title = moderationFix.note;

  return (
    <div className="flex items-start gap-1 leading-[1.4] text-yellow-11" title={title} data-testid={testId}>
      <Icon icon={IconVariants.ALERT_TRIANGLE} size={IconSize.XSMALL} className="mt-px flex-none" />
      <Text as="span" variant={TextVariants.LABEL_SMALL_REGULAR}>
        {message}
        {flaggedTextLabel ? <span> · {flaggedTextLabel}</span> : null}
      </Text>
    </div>
  );
};

export default memo(RulebookListCardModerationRow);
